import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  Search, Filter, Eye, Ban, CheckCircle, XCircle, 
  Calendar, Mail, Phone, MapPin, FileText, Activity,
  ChevronLeft, ChevronRight
} from 'lucide-react';
import { Card, CardContent } from '../../components/ui/card';
import { Badge } from '../../components/ui/badge';
import { Button } from '../../components/ui/button';

export default function PatientManagement() {
  const navigate = useNavigate();
  const [patients, setPatients] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedPatient, setSelectedPatient] = useState(null);
  const [showDetail, setShowDetail] = useState(false);
  
  // Filters
  const [searchTerm, setSearchTerm] = useState('');
  const [statusFilter, setStatusFilter] = useState('');
  const [showFilters, setShowFilters] = useState(true);
  
  // Pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPatients, setTotalPatients] = useState(0);
  const patientsPerPage = 20;

  useEffect(() => {
    fetchPatients();
  }, [currentPage, statusFilter]);

  const fetchPatients = async () => {
    try {
      setLoading(true);
      const skip = (currentPage - 1) * patientsPerPage;
      
      let url = `${process.env.REACT_APP_BACKEND_URL}/api/admin/patients?skip=${skip}&limit=${patientsPerPage}`;
      if (statusFilter) {
        url += `&status=${statusFilter}`;
      }
      if (searchTerm) {
        url += `&search=${encodeURIComponent(searchTerm)}`;
      }
      
      const response = await fetch(url, {
        headers: { }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch patients');
      }

      const data = await response.json();
      setPatients(data.patients);
      setTotalPatients(data.total);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = () => {
    setCurrentPage(1);
    fetchPatients();
  };

  const handleStatusUpdate = async (patientId, newStatus, reason = '') => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/patients/${patientId}/status?status=${newStatus}${reason ? `&reason=${encodeURIComponent(reason)}` : ''}`,
        {
          method: 'PATCH',
          headers: { 
            'Content-Type': 'application/json'
          }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to update status');
      }

      // Refresh the list
      fetchPatients();
      alert(`Patient status updated to ${newStatus}`);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const viewPatientDetail = async (patientId) => {
    try {
      const response = await fetch(
        `${process.env.REACT_APP_BACKEND_URL}/api/admin/patients/${patientId}`,
        {
          headers: { }
        }
      );

      if (!response.ok) {
        throw new Error('Failed to fetch patient details');
      }

      const data = await response.json();
      setSelectedPatient(data);
      setShowDetail(true);
    } catch (err) {
      alert(`Error: ${err.message}`);
    }
  };

  const getStatusBadge = (status) => {
    const statusConfig = {
      'verified': { color: 'bg-green-100 text-green-800', label: 'Verified' },
      'pending': { color: 'bg-yellow-100 text-yellow-800', label: 'Pending' },
      'suspended': { color: 'bg-red-100 text-red-800', label: 'Suspended' },
      'active': { color: 'bg-blue-100 text-blue-800', label: 'Active' }
    };
    
    const config = statusConfig[status] || statusConfig['pending'];
    return <Badge className={config.color}>{config.label}</Badge>;
  };

  const totalPages = Math.ceil(totalPatients / patientsPerPage);

  if (showDetail && selectedPatient) {
    return <PatientDetailView patient={selectedPatient} onBack={() => setShowDetail(false)} onStatusUpdate={handleStatusUpdate} />;
  }

  return (
    <div className="space-y-6" data-testid="patient-management">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold text-gray-900">Patient Management</h1>
          <p className="text-gray-600 mt-1">Manage patient accounts and monitor activity</p>
        </div>
        <Badge className="bg-blue-100 text-blue-800 px-4 py-2">
          {totalPatients} Total Patients
        </Badge>
      </div>

      {/* Filters Section */}
      <Card className="border-2 border-blue-200">
        <CardContent className="p-6">
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-gray-900">Filters & Search</h3>
            <Button
              variant="outline"
              onClick={() => setShowFilters(!showFilters)}
              data-testid="toggle-filters"
            >
              <Filter className="w-4 h-4 mr-2" />
              {showFilters ? 'Hide' : 'Show'} Filters
            </Button>
          </div>

          {showFilters && (
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {/* Search */}
              <div className="md:col-span-2">
                <label className="block text-sm font-medium text-gray-700 mb-2">Search</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-5 h-5 text-gray-400" />
                    <input
                      type="text"
                      value={searchTerm}
                      onChange={(e) => setSearchTerm(e.target.value)}
                      onKeyPress={(e) => e.key === 'Enter' && handleSearch()}
                      placeholder="Search by name or email..."
                      className="w-full pl-10 pr-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                      data-testid="search-input"
                    />
                  </div>
                  <Button onClick={handleSearch} data-testid="search-button">
                    Search
                  </Button>
                </div>
              </div>

              {/* Status Filter */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">Status</label>
                <select
                  value={statusFilter}
                  onChange={(e) => setStatusFilter(e.target.value)}
                  className="w-full px-4 py-2 border-2 border-gray-200 rounded-lg focus:border-blue-500 focus:ring-2 focus:ring-blue-200 outline-none"
                  data-testid="status-filter"
                >
                  <option value="">All Statuses</option>
                  <option value="verified">Verified</option>
                  <option value="pending">Pending</option>
                  <option value="suspended">Suspended</option>
                  <option value="active">Active</option>
                </select>
              </div>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Patients Table */}
      <Card>
        <CardContent className="p-0">
          {loading ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="text-6xl mb-4">⏳</div>
                <p className="text-gray-600">Loading patients...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <XCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
                <p className="text-red-600">{error}</p>
              </div>
            </div>
          ) : patients.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <FileText className="w-16 h-16 text-gray-400 mx-auto mb-4" />
                <p className="text-gray-600">No patients found</p>
              </div>
            </div>
          ) : (
            <>
              <div className="overflow-x-auto">
                <table className="w-full" data-testid="patients-table">
                  <thead className="bg-gray-50 border-b-2 border-gray-200">
                    <tr>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Patient</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Contact</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Status</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Joined</th>
                      <th className="px-6 py-4 text-left text-sm font-semibold text-gray-900">Treatments</th>
                      <th className="px-6 py-4 text-center text-sm font-semibold text-gray-900">Actions</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-200">
                    {patients.map((patient, index) => (
                      <tr key={index} className="hover:bg-gray-50" data-testid={`patient-row-${index}`}>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-3">
                            <div className="w-10 h-10 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-semibold">
                              {patient.email[0].toUpperCase()}
                            </div>
                            <div>
                              <p className="font-semibold text-gray-900">{patient.name || 'N/A'}</p>
                              <p className="text-sm text-gray-500">{patient.email}</p>
                            </div>
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <div className="space-y-1">
                            {patient.profile?.phone && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <Phone className="w-4 h-4" />
                                {patient.profile.phone}
                              </div>
                            )}
                            {patient.profile?.country && (
                              <div className="flex items-center gap-2 text-sm text-gray-600">
                                <MapPin className="w-4 h-4" />
                                {patient.profile.country}
                              </div>
                            )}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          {getStatusBadge(patient.account_status)}
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center gap-2 text-sm text-gray-600">
                            <Calendar className="w-4 h-4" />
                            {new Date(patient.created_at).toLocaleDateString()}
                          </div>
                        </td>
                        <td className="px-6 py-4">
                          <Badge variant="outline" className="text-blue-600">
                            {patient.profile?.treatment_count || 0} treatments
                          </Badge>
                        </td>
                        <td className="px-6 py-4">
                          <div className="flex items-center justify-center gap-2">
                            <Button
                              size="sm"
                              variant="outline"
                              onClick={() => viewPatientDetail(patient._id)}
                              data-testid={`view-patient-${index}`}
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {patient.account_status !== 'suspended' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-red-600 border-red-600 hover:bg-red-50"
                                onClick={() => {
                                  if (window.confirm('Are you sure you want to suspend this patient?')) {
                                    handleStatusUpdate(patient._id, 'suspended', 'Suspended by admin');
                                  }
                                }}
                              >
                                <Ban className="w-4 h-4" />
                              </Button>
                            )}
                            {patient.account_status === 'suspended' && (
                              <Button
                                size="sm"
                                variant="outline"
                                className="text-green-600 border-green-600 hover:bg-green-50"
                                onClick={() => handleStatusUpdate(patient._id, 'verified')}
                              >
                                <CheckCircle className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>

              {/* Pagination */}
              <div className="flex items-center justify-between px-6 py-4 border-t border-gray-200">
                <div className="text-sm text-gray-600">
                  Showing {((currentPage - 1) * patientsPerPage) + 1} to {Math.min(currentPage * patientsPerPage, totalPatients)} of {totalPatients} patients
                </div>
                <div className="flex items-center gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage - 1)}
                    disabled={currentPage === 1}
                    data-testid="prev-page"
                  >
                    <ChevronLeft className="w-4 h-4" />
                    Previous
                  </Button>
                  <div className="flex items-center gap-1">
                    {[...Array(Math.min(5, totalPages))].map((_, i) => {
                      const pageNum = i + 1;
                      return (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(pageNum)}
                          className={`w-8 h-8 rounded ${
                            currentPage === pageNum
                              ? 'bg-blue-600 text-white'
                              : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                          }`}
                        >
                          {pageNum}
                        </button>
                      );
                    })}
                  </div>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setCurrentPage(currentPage + 1)}
                    disabled={currentPage === totalPages}
                    data-testid="next-page"
                  >
                    Next
                    <ChevronRight className="w-4 h-4" />
                  </Button>
                </div>
              </div>
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
}

// Patient Detail View Component
function PatientDetailView({ patient, onBack, onStatusUpdate }) {
  return (
    <div className="space-y-6" data-testid="patient-detail-view">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-4">
          <Button variant="outline" onClick={onBack} data-testid="back-button">
            <ChevronLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Patient Details</h1>
            <p className="text-gray-600 mt-1">Complete patient information and activity</p>
          </div>
        </div>
        <div className="flex items-center gap-2">
          <Badge className="px-4 py-2">
            {patient.patient.account_status}
          </Badge>
        </div>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Patient Info */}
        <div className="lg:col-span-1 space-y-6">
          {/* Basic Info Card */}
          <Card className="border-2 border-blue-200">
            <CardContent className="p-6">
              <div className="text-center mb-6">
                <div className="w-24 h-24 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white font-bold text-3xl mx-auto mb-4">
                  {patient.patient.email[0].toUpperCase()}
                </div>
                <h2 className="text-xl font-bold text-gray-900">{patient.patient.name || 'N/A'}</h2>
                <p className="text-gray-600">{patient.patient.email}</p>
              </div>

              <div className="space-y-4">
                <div className="flex items-center justify-between py-2 border-b border-gray-200">
                  <span className="text-sm text-gray-600">Member Since</span>
                  <span className="font-semibold">{new Date(patient.patient.created_at).toLocaleDateString()}</span>
                </div>
                {patient.profile?.phone && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Phone</span>
                    <span className="font-semibold">{patient.profile.phone}</span>
                  </div>
                )}
                {patient.profile?.country && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Country</span>
                    <span className="font-semibold">{patient.profile.country}</span>
                  </div>
                )}
                {patient.profile?.date_of_birth && (
                  <div className="flex items-center justify-between py-2 border-b border-gray-200">
                    <span className="text-sm text-gray-600">Date of Birth</span>
                    <span className="font-semibold">{new Date(patient.profile.date_of_birth).toLocaleDateString()}</span>
                  </div>
                )}
              </div>

              {/* Actions */}
              <div className="mt-6 space-y-2">
                {patient.patient.account_status !== 'suspended' && (
                  <Button
                    variant="outline"
                    className="w-full text-red-600 border-red-600 hover:bg-red-50"
                    onClick={() => {
                      if (window.confirm('Suspend this patient account?')) {
                        onStatusUpdate(patient.patient._id, 'suspended', 'Admin action');
                      }
                    }}
                  >
                    <Ban className="w-4 h-4 mr-2" />
                    Suspend Account
                  </Button>
                )}
                {patient.patient.account_status === 'suspended' && (
                  <Button
                    variant="outline"
                    className="w-full text-green-600 border-green-600 hover:bg-green-50"
                    onClick={() => onStatusUpdate(patient.patient._id, 'verified')}
                  >
                    <CheckCircle className="w-4 h-4 mr-2" />
                    Reactivate Account
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>

          {/* Activity Stats */}
          <Card>
            <CardContent className="p-6">
              <h3 className="font-semibold text-gray-900 mb-4">Activity Stats</h3>
              <div className="space-y-4">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Treatments</span>
                  <Badge variant="outline">{patient.treatments?.length || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Appointments</span>
                  <Badge variant="outline">{patient.appointments?.length || 0}</Badge>
                </div>
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Payments</span>
                  <Badge variant="outline">{patient.payments?.length || 0}</Badge>
                </div>
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Right Column - Activity Details */}
        <div className="lg:col-span-2 space-y-6">
          {/* Treatments */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Treatment Requests</h3>
              {patient.treatments && patient.treatments.length > 0 ? (
                <div className="space-y-3">
                  {patient.treatments.slice(0, 5).map((treatment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">{treatment.treatment_type || 'N/A'}</p>
                        <p className="text-sm text-gray-600">Urgency: {treatment.urgency}</p>
                      </div>
                      <Badge className={
                        treatment.status === 'completed' ? 'bg-green-100 text-green-800' :
                        treatment.status === 'in_progress' ? 'bg-blue-100 text-blue-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {treatment.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No treatment requests</p>
              )}
            </CardContent>
          </Card>

          {/* Appointments */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Appointments</h3>
              {patient.appointments && patient.appointments.length > 0 ? (
                <div className="space-y-3">
                  {patient.appointments.slice(0, 5).map((appointment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">
                          {new Date(appointment.appointment_date).toLocaleDateString()}
                        </p>
                        <p className="text-sm text-gray-600">{appointment.appointment_time}</p>
                      </div>
                      <Badge>{appointment.status}</Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No appointments</p>
              )}
            </CardContent>
          </Card>

          {/* Payments */}
          <Card>
            <CardContent className="p-6">
              <h3 className="text-lg font-semibold text-gray-900 mb-4">Payment History</h3>
              {patient.payments && patient.payments.length > 0 ? (
                <div className="space-y-3">
                  {patient.payments.slice(0, 5).map((payment, idx) => (
                    <div key={idx} className="flex items-center justify-between p-4 bg-gray-50 rounded-lg">
                      <div>
                        <p className="font-semibold text-gray-900">${payment.amount}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(payment.created_at).toLocaleDateString()}
                        </p>
                      </div>
                      <Badge className={
                        payment.status === 'paid' ? 'bg-green-100 text-green-800' :
                        payment.status === 'failed' ? 'bg-red-100 text-red-800' :
                        'bg-yellow-100 text-yellow-800'
                      }>
                        {payment.status}
                      </Badge>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-gray-500 text-center py-8">No payment history</p>
              )}
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
}
